<template>
  <form @submit.prevent="verifyOtp">
    <div class="msg">
      Enter the 6-digit code sent to<br />
      <strong>{{ email }}</strong>
    </div>

    <div v-if="otpPrefix" class="otp-hint">
      Use the code that starts with <strong>{{ otpPrefix }}</strong>
    </div>

    <div class="otp-row" @paste.prevent="onPaste">
      <!-- <span v-if="otpPrefix" class="otp-prefix" :title="`Code prefix ${otpPrefix}`"
        >{{ otpPrefix }}<span class="otp-dash">-</span></span
      > -->
      <div class="otp-inputs">
        <input
          v-for="(digit, i) in digits"
          :key="i"
          :ref="(el) => (inputs[i] = el)"
          v-model="digits[i]"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          class="otp-input"
          :class="{ 'is-invalid': error }"
          :aria-label="`Digit ${i + 1}`"
          @input="onInput(i, $event)"
          @keydown="onKeydown(i, $event)"
          @focus="$event.target.select()"
        />
      </div>
    </div>

    <div v-if="error" class="text-danger text-center m-b-10">{{ error }}</div>

    <button
      class="btn btn-block btn-ghipss waves-effect"
      :disabled="code.length !== OTP_LENGTH || verifying"
    >
      VERIFY
    </button>

    <div class="row m-t-15">
      <div class="col-xs-6">
        <a href="javascript:void(0);" class="col-ghipss" @click="$emit('cancel')"
          >Back to login</a
        >
      </div>
      <div class="col-xs-6 text-right">
        <span v-if="cooldown > 0" class="col-grey">Resend in {{ cooldown }}s</span>
        <a v-else href="javascript:void(0);" class="col-ghipss" @click="$emit('resend')"
          >Resend code</a
        >
      </div>
    </div>
  </form>
</template>

<script>
import axios from "@/axios.js";

// Request:  { otpPrefix, otp, requestId }
// Response: { generalResponse: { errorCode, errorMessage }, userDTO }
//           where errorCode "0" means the code was accepted.
const VERIFY_OTP_URL = "user/verify_otp";
const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;

export default {
  name: "OtpVerification",
  props: {
    email: { type: String, required: true },
    // May be empty until the backend sends it; the prefix UI hides then
    otpPrefix: { type: String, default: "" },
    requestId: { type: String, required: true },
  },
  // verified(result): result is the verify-OTP response body
  emits: ["verified", "resend", "cancel"],
  data() {
    return {
      OTP_LENGTH,
      digits: Array(OTP_LENGTH).fill(""),
      inputs: [],
      error: "",
      wrongPrefix: "",
      verifying: false,
      cooldown: RESEND_COOLDOWN_SECONDS,
      timer: null,
    };
  },
  computed: {
    code() {
      return this.digits.join("");
    },
  },
  methods: {
    // Pulls the 6-digit code out of pasted or autofilled text.
    // Accepts "693467", "693 467", "693-467", "A562-693467", or a sentence
    // with exactly one such code in it. Returns null for anything else.
    parseCode(text) {
      this.wrongPrefix = "";
      const value = String(text || "").trim();
      if (!value) return null;

      // 1. The code right after our prefix, e.g. "A562-693467"
      const prefix = (this.otpPrefix || "").trim();
      if (prefix) {
        const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const m = value.match(
          new RegExp(escaped + "[\\s-]*(\\d{3})[\\s-]?(\\d{3})(?!\\d)", "i"),
        );
        if (m) return m[1] + m[2];

        // A code with a different prefix is from an older email
        const other = value.match(/([A-Za-z0-9]*[A-Za-z][A-Za-z0-9]*)-\d{3}[\s-]?\d{3}(?!\d)/);
        if (other) {
          this.wrongPrefix = other[1].toUpperCase();
          return null;
        }
      }

      // 2. Just the code, optionally split in the middle: "693 467"
      if (/^\d{3}[\s-]?\d{3}$/.test(value)) return value.replace(/\D/g, "");

      // 3. A sentence containing exactly one standalone 6-digit number
      const found = [...value.matchAll(/(?:^|\D)(\d{6})(?!\d)/g)];
      return found.length === 1 ? found[0][1] : null;
    },
    invalidPasteMessage() {
      if (this.wrongPrefix) {
        return `That code (${this.wrongPrefix}) is from an older email. Use the code that starts with ${this.otpPrefix}.`;
      }
      const example = this.otpPrefix ? `${this.otpPrefix}-123456` : "123456";
      return `That doesn't look like a code. Paste the 6-digit code from your email (e.g. ${example}).`;
    },
    // Writes digits starting at box i, moves focus past them, and
    // submits once all boxes are filled.
    fillFrom(i, value) {
      const chars = value.slice(0, OTP_LENGTH - i).split("");
      chars.forEach((c, k) => (this.digits[i + k] = c));
      this.error = "";
      const next = Math.min(i + chars.length, OTP_LENGTH - 1);
      if (chars.length) this.inputs[next].focus();
      if (this.code.length === OTP_LENGTH) this.verifyOtp();
    },
    // Fallback for mobile keyboards and autofill, which may not send usable
    // keydown events and can drop the whole code into one box.
    onInput(i, event) {
      const raw = event.target.value;
      this.digits[i] = "";
      if (raw.length > 1) {
        const code = this.parseCode(raw);
        if (code) return this.fillFrom(0, code);
      }
      // Otherwise keep only the newest digit typed into this box
      const digit = raw.replace(/\D/g, "").slice(-1);
      if (digit) this.fillFrom(i, digit);
    },
    onKeydown(i, event) {
      // Handle digits here so focus moves before the next key arrives
      if (/^\d$/.test(event.key)) {
        event.preventDefault();
        this.fillFrom(i, event.key);
      } else if (event.key === "Backspace" && !this.digits[i] && i > 0) {
        this.inputs[i - 1].focus();
      } else if (event.key === "ArrowLeft" && i > 0) {
        this.inputs[i - 1].focus();
      } else if (event.key === "ArrowRight" && i < OTP_LENGTH - 1) {
        this.inputs[i + 1].focus();
      }
    },
    onPaste(event) {
      const code = this.parseCode(event.clipboardData.getData("text"));
      if (!code) {
        this.error = this.invalidPasteMessage();
        return;
      }
      this.digits = Array(OTP_LENGTH).fill("");
      this.fillFrom(0, code);
    },
    clearDigits() {
      this.digits = Array(OTP_LENGTH).fill("");
      this.$nextTick(() => this.inputs[0].focus());
    },

    async verifyOtp() {
      if (this.code.length !== OTP_LENGTH || this.verifying) return;
      this.verifying = true;
      $.LoadingOverlay("show");
      try {
        const response = await axios.post(VERIFY_OTP_URL, {
          otpPrefix: this.otpPrefix,
          otp: this.code,
          requestId: this.requestId,
        });
        const result = response.data || {};
        const general = result.generalResponse || {};
        if (general.errorCode == "0") {
          this.$emit("verified", result);
        } else {
          this.error = general.errorMessage || "Invalid code. Please try again.";
          this.clearDigits();
        }
      } catch (err) {
        console.log(err);
        // A wrong or expired code comes back as a 401 with the reason in
        // the body, e.g. { errorCode: "1", errorMessage: "Invalid or expired OTP challenge" }
        const data = (err.response && err.response.data) || {};
        const message = data.errorMessage || (data.generalResponse || {}).errorMessage;
        this.error = message || (err.response
          ? "Invalid code. Please try again."
          : "An unexpected error occurred...Try again!");
        this.clearDigits();
      } finally {
        $.LoadingOverlay("hide");
        this.verifying = false;
      }
    },

    startCooldown() {
      clearInterval(this.timer);
      this.cooldown = RESEND_COOLDOWN_SECONDS;
      this.timer = setInterval(() => {
        this.cooldown -= 1;
        if (this.cooldown <= 0) clearInterval(this.timer);
      }, 1000);
    },
  },
  mounted() {
    this.startCooldown();
    this.inputs[0].focus();
  },
  beforeUnmount() {
    clearInterval(this.timer);
  },
};
</script>

<style scoped>
.otp-hint {
  text-align: center;
  color: #777;
  font-size: 13px;
}
.otp-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 15px 0;
}
.otp-prefix {
  flex: none;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #075084;
  white-space: nowrap;
}
.otp-dash {
  margin-left: 4px;
  color: #999;
}
.otp-inputs {
  flex: 1;
  display: flex;
  justify-content: space-between;
  gap: 6px;
}
.otp-input {
  width: 100%;
  min-width: 0;
  max-width: 44px;
  height: 50px;
  text-align: center;
  font-size: 20px;
  font-weight: 700;
  border: 1px solid #ccc;
  border-radius: 6px;
  outline: none;
}
.otp-input:focus {
  border-color: #075084;
  box-shadow: 0 0 0 2px rgba(7, 80, 132, 0.2);
}
.otp-input.is-invalid {
  border-color: #f44336;
}
button[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
